import { mutations, queries } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import type { MutationDeleteUsersCustomerAdminArgs } from "@/types/graphql";

/**
 * Custom hook to delete a user company.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{deleteUserCompany: Function, deleting: boolean}} - The function to delete a user company and the loading state.
 */
export function useDeleteUserCompany(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const [deleteUserCompany, { loading }] = useMutation<any>(
    mutations.deleteUsersCustomerAdmin,
    {
      onCompleted,
      onError: (err) => {
        onError(err);

        enqueueSnackbar("Delete User Company: " + err.message, {
          variant: "error",
        });
      },
      errorPolicy: "all",
    },
  );

  const deleteUserCompanyRow = useCallback(
    (variables: MutationDeleteUsersCustomerAdminArgs) => {
      deleteUserCompany({
        variables,
        refetchQueries: [
          {
            query: queries.getUserProfile,
            variables: {
              userId: variables.userId,
            },
          },
        ],
      }).then();
    },
    [deleteUserCompany],
  );

  return {
    deleteUserCompany: deleteUserCompanyRow,
    deleting: loading,
  };
}
